import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { isNotNullOrUndefined } from 'utils/utils';
import { hasLockType, hasNoLocks } from 'utils/lockUtils';
import { LockTypes, PartnerLockType } from 'types/PartnerLockTypes';
import DraftSwitch, { DlmStatus, isVersionADraft } from 'views/DraftMode/DraftSwitch';
import { KycLockMessage } from 'components/Common/KycLockMessage';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { GlobalState } from 'reducers/stateTypes';
import { User } from 'types/User';
import { selectDlmStatus } from 'selectors/dlmSelector';
import { useFetchPartnerLockStatus } from 'hooks/queries/useFetchPartnerLockStatus';
import { usePartnerDlmV2Enabled } from 'hooks/queries/usePartnerDlmV2Enabled';
import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { PartnerStatusDto } from 'views/RevisionHistory/Partner/PartnerRevisionDtos';
import { setDlmStatusAndVersion } from 'reducers/dlmReducer';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { SMALL_SCREEN_WIDTH } from 'services/constants';
import { ClmPortalIncomingPostMessageTypes } from 'utils/postMessageUtil';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';

type LockProps = {
  techId: string;
  partnerLocks?: PartnerLockType[];
  shouldShowDraftSwitch?: boolean;
};

const shouldShowDraftButtons = (partnerLocks: PartnerLockType[] | undefined, partnerVersions: PartnerStatusDto[]) => {
  return (
    hasNoLocks(partnerLocks) ||
    hasLockType(partnerLocks, LockTypes.USER) ||
    partnerVersions.find((ver) => isVersionADraft(ver.partnerStatus))
  );
};

const findDlmStateByStatus = (version: DlmStatus | undefined, partnerRevisions: PartnerStatusDto[]) => {
  return partnerRevisions.find((partnerRevision: PartnerStatusDto) => partnerRevision.partnerStatus === version);
};

export const Lock = ({ techId, partnerLocks = [], shouldShowDraftSwitch = true }: LockProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const runtimeConfig = useRuntimeConfig();
  const isPartnerLockEnabled = runtimeConfig.configData.featureFlags?.partnerLock?.enabled;
  const isPartnerDlmV2 = usePartnerDlmV2Enabled();
  const user: User = useSelector((state: GlobalState) => state.user?.userDetails);
  const selectedDlmStatus = useSelector(selectDlmStatus);
  const { isSuccess: isSuccessPartnerLock, refetch: refetchPartnerLock } = useFetchPartnerLockStatus(techId);
  const {
    data: partnerVersions = [],
    refetch: refetchVersions,
    isSuccess: isSuccessPartnerVersions,
  } = useFetchPartnerVersions(techId);
  const smallScreen = useMediaQuery(`(max-width:${SMALL_SCREEN_WIDTH})`);

  const statusOrder = ['ACTIVE', 'DRAFT', 'DRAFT_REVIEW'];
  const appendActive = (versions: PartnerStatusDto[]) =>
    versions.some((data: PartnerStatusDto) => data.partnerStatus === 'ACTIVE')
      ? versions
      : versions.concat({
          partnerUid: techId,
          version: 1,
          partnerStatus: 'ACTIVE',
          modifiedBy: user?.username,
          modifiedTimestamp: new Date().toISOString(),
          createdTimestamp: new Date().toISOString(),
          reason: null,
        } as PartnerStatusDto);

  const sortedPartnerRevisions = useMemo(() => {
    const sortedVersions = partnerVersions
      .filter((partnerVersion: PartnerStatusDto) => statusOrder.includes(partnerVersion.partnerStatus))
      .sort((a, b) => {
        return statusOrder.indexOf(a.partnerStatus) - statusOrder.indexOf(b.partnerStatus);
      });
    return isNotNullOrUndefined(sortedVersions) ? appendActive(sortedVersions) : [];
  }, [partnerVersions, isSuccessPartnerVersions]);

  // DLM v2: the iframe owns status/version, so the portal stops driving the dlm store.
  useEffect(() => {
    if (isPartnerDlmV2 !== false) {
      return;
    }
    if (isSuccessPartnerLock && isSuccessPartnerVersions && !!selectedDlmStatus) {
      const dlmState = findDlmStateByStatus(selectedDlmStatus, sortedPartnerRevisions);

      if (isNotNullOrUndefined(dlmState)) {
        dispatch(
          setDlmStatusAndVersion({
            status: dlmState.partnerStatus,
            version: dlmState.version,
          }),
        );
      }
    }
  }, [
    selectedDlmStatus,
    partnerVersions,
    partnerLocks,
    isSuccessPartnerLock,
    isSuccessPartnerVersions,
    isPartnerDlmV2,
  ]);

  const userLock = Array.isArray(partnerLocks)
    ? partnerLocks.find((lockStatus: PartnerLockType) => lockStatus?.lockType === LockTypes.USER)
    : undefined;

  const isPartnerLocked = isNotNullOrUndefined(userLock?.lockContext?.userId);
  const isCurrentUserLocked = userLock?.lockContext?.userId === user?.username;

  useEffect(() => {
    if (isPartnerDlmV2 !== false) {
      return;
    }
    if (isSuccessPartnerLock && isNotNullOrUndefined(user)) {
      const targetStatus = isPartnerLocked && isCurrentUserLocked ? 'DRAFT' : 'ACTIVE';
      const targetVersion = partnerVersions.find((x) => x.partnerStatus === targetStatus)?.version;

      dispatch(setDlmStatusAndVersion({ status: targetStatus, version: targetVersion }));
    }
  }, [isSuccessPartnerLock, isPartnerLocked, isCurrentUserLocked, user, isPartnerDlmV2]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    async function handleMessage(event: MessageEvent) {
      const message = event.data.message;
      if (message === ClmPortalIncomingPostMessageTypes.RE_FETCH_PARTNER_LOCK_STATUS) {
        const { data: refetchedLock } = await refetchPartnerLock();
        const { data: refetchedVersions } = await refetchVersions();

        // DLM v2: the iframe owns status/version — refresh the lock data, but don't drive the dlm store.
        if (isPartnerDlmV2 !== false) {
          return;
        }
        const userLock = Array.isArray(refetchedLock)
          ? refetchedLock.find((lockStatus: PartnerLockType) => lockStatus?.lockType === LockTypes.USER)
          : undefined;
        const versionToShow = userLock?.lockContext?.userId === user?.username ? 'DRAFT' : 'ACTIVE';
        const refetchedWithActive =
          isNotNullOrUndefined(refetchedVersions) &&
          refetchedVersions.some((data: PartnerStatusDto) => data.partnerStatus === 'ACTIVE')
            ? refetchedVersions
            : appendActive(refetchedVersions ?? sortedPartnerRevisions);
        const activeVersion = findDlmStateByStatus(versionToShow, refetchedWithActive);
        if (isNotNullOrUndefined(activeVersion)) {
          dispatch(
            setDlmStatusAndVersion({
              status: activeVersion.partnerStatus,
              version: activeVersion.version,
            }),
          );
        }
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user, isPartnerDlmV2]);

  // DLM v2: the iframe owns draft/view mode, so the portal switch is hidden.
  const showDraftSwitch =
    isPartnerLockEnabled &&
    isPartnerDlmV2 === false &&
    shouldShowDraftSwitch &&
    shouldShowDraftButtons(partnerLocks, partnerVersions);

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={8}>
      {/* DLM v2: each module shows the KYC lock in its own footer. */}
      {isPartnerLockEnabled && isPartnerDlmV2 === false && hasLockType(partnerLocks, LockTypes.KYC) && (
        <Box>
          <KycLockMessage />
        </Box>
      )}
      {showDraftSwitch && !!userLock?.lockContext?.userId && !isCurrentUserLocked && (
        <Box textAlign={smallScreen ? 'center' : 'right'} mr={0}>
          <JBIcon iconName="lock" fill={JBColors.support.red[120]} />
          <Typography
            variant="h6"
            color={JBColors.support.red[120]}
            display="inline"
            id="in-editing-text"
            data-testid="in-editing-text"
            sx={{ marginLeft: '8px' }}
          >
            {t('topbar.versioning.inEditing', 'In editing')} ({userLock?.lockContext?.userId})
          </Typography>
        </Box>
      )}
      {showDraftSwitch && (
        <Box>
          <DraftSwitch partnerUid={techId} partnerLocks={partnerLocks} />
        </Box>
      )}
    </Box>
  );
};
